/**
 * The help / wrong-answer dialogs (moved verbatim from StepView.vue). Pressing
 * OK calls `onDismiss(kind)` — the answer flow presents the next try (D-6).
 */
import { toValue, type MaybeRefOrGetter } from 'vue';
import { useQuasar } from 'quasar';
import { t } from 'boot/i18n';
import StepDialogCard from '../components/StepDialogCard/StepDialogCard.vue';

/** The drill settings the dialogs read (a DrillSettings satisfies it). */
export interface FeedbackDialogsSettings {
  handling_of_mistakes?: string;
}

export type FeedbackDialogKind = 'help' | 'error' | 'comparison';

export interface UseDrillFeedbackDialogsOptions {
  drillSettings: MaybeRefOrGetter<FeedbackDialogsSettings | null | undefined>;
  /** The student pressed OK on the dialog. */
  onDismiss: (kind: FeedbackDialogKind) => void;
}

export interface ComparisonDialogContent {
  subTitle: string;
  yourAnswer: string;
  correctAnswer: string;
}

export interface UseDrillFeedbackDialogsReturn {
  openHelp: (correctAnswer: string | string[] | null | undefined) => void;
  openError: () => void;
  openComparison: (content: ComparisonDialogContent) => void;
}

export function useDrillFeedbackDialogs({
  drillSettings,
  onDismiss,
}: UseDrillFeedbackDialogsOptions): UseDrillFeedbackDialogsReturn {
  const $q = useQuasar();

  function openHelp(correctAnswer: string | string[] | null | undefined) {
    $q.dialog({
      component: StepDialogCard,
      componentProps: {
        dark: true,
        title: t('HELP'),
        correctAnswer,
        persistent: true,
        message:
          toValue(drillSettings)?.handling_of_mistakes ===
          'Allow Correction of Mistakes'
            ? undefined
            : t('IT_WAS_WRITTEN_AS_AN_ERROR_AND_IMPACT_YOUR_SCORE'),
      },
    }).onOk(() => {
      onDismiss('help');
    });
  }

  function openError() {
    $q.dialog({
      component: StepDialogCard,
      componentProps: {
        dark: true,
        title: t('WRONG_ANSWER'),
        subTitle: t('OOPS_THIS_IS_THE_WRONG_ANSWER'),
        persistent: true,
      },
    }).onOk(() => {
      onDismiss('error');
    });
  }

  function openComparison({
    subTitle,
    yourAnswer,
    correctAnswer,
  }: ComparisonDialogContent) {
    $q.dialog({
      component: StepDialogCard,
      componentProps: {
        dark: true,
        title: t('WRONG_ANSWER'),
        subTitle,
        yourAnswer,
        correctAnswer,
        persistent: true,
      },
    }).onOk(() => {
      onDismiss('comparison');
    });
  }

  return { openHelp, openError, openComparison };
}
